import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/layout/AppShell';
import { LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Shield, Award, FileText, Download, TrendingDown, DollarSign, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function TraceabilityReportPage() {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/rfx/workflow/traceability-report');
      setData(r.data);
    } catch { toast.error('Failed to load traceability report'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtMoney = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const exportExcel = async () => {
    if (!data?.awards?.length) return;
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      const headers = ['Event Ref', 'Type', 'Title', 'Category', 'Currency', 'Awarded To', 'Awarded By', 'Awarded At', 'Notes', 'Baseline', 'Awarded Value', 'Saving', 'Saving %', 'Baseline Method', 'Requisitions', 'Created By'];
      const rows = data.awards.map(a => [
        a.event_ref, a.event_type, a.event_title, a.category, a.currency,
        (a.awarded_to || []).join(', '), a.awarded_by, a.awarded_at, a.notes,
        a.baseline, a.awarded_value, a.saving_amount, a.saving_pct + '%',
        a.baseline_method, (a.requisitions || []).map(r => r.number).join(', '), a.created_by,
      ]);

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      XLSX.utils.book_append_sheet(wb, ws, 'Award Traceability');

      XLSX.writeFile(wb, `Award_Traceability_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Report exported to Excel');
    } catch (e) { toast.error('Export failed: ' + e.message); }
  };

  const exportPDF = () => {
    const content = document.getElementById('traceability-content');
    if (!content) return;
    const pw = window.open('', '_blank');
    pw.document.write(`<!DOCTYPE html><html><head><title>Award Decision Traceability Report</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
        h1 { font-size: 20px; color: #1e3a5f; } h2 { font-size: 16px; color: #1e3a5f; margin-top: 24px; border-bottom: 2px solid #e5e7eb; padding-bottom: 4px; }
        .subtitle { font-size: 12px; color: #666; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 11px; }
        th { background: #f8f9fa; padding: 6px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #e5e7eb; }
        td { padding: 5px 8px; border-bottom: 1px solid #f0f0f0; }
        .right { text-align: right; } .mono { font-family: monospace; } .green { color: #059669; }
        .kpi { display: inline-block; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 20px; text-align: center; margin-right: 12px; }
        .kpi .value { font-size: 20px; font-weight: 700; } .kpi .label { font-size: 10px; color: #666; }
      </style></head><body>
      <h1>Award Decision Traceability Report</h1>
      <p class="subtitle">Generated: ${new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz })} | ${data?.summary?.total_awards || 0} award decisions</p>
      ${content.innerHTML}
    </body></html>`);
    pw.document.close();
    setTimeout(() => pw.print(), 500);
  };

  if (loading) return <AppShell><div className="flex items-center justify-center h-96"><LoadingSpinner /></div></AppShell>;
  if (!data) return <AppShell><p className="text-center text-slate-400 py-12">No data available.</p></AppShell>;

  return (
    <AppShell>
      <div className="p-4 sm:p-6 space-y-4" data-testid="traceability-report-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Shield className="w-6 h-6 text-indigo-600" /> Award Decision Traceability
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Full audit trail of all sourcing award decisions with downstream procurement links</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-trace-excel">
              <Download className="w-3.5 h-3.5 mr-1" /> Export Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportPDF} data-testid="export-trace-pdf">
              <FileText className="w-3.5 h-3.5 mr-1" /> Save as PDF
            </Button>
          </div>
        </div>

        <div id="traceability-content">
          {/* KPI Summary */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div className="border rounded-xl p-4 text-center bg-white">
              <Award className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-indigo-700">{data.summary.total_awards}</p>
              <p className="text-xs text-slate-500">Total Awards</p>
            </div>
            <div className="border rounded-xl p-4 text-center bg-white">
              <DollarSign className="w-5 h-5 text-slate-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-slate-700">{fmtMoney(data.summary.total_baseline)}</p>
              <p className="text-xs text-slate-500">Total Baseline</p>
            </div>
            <div className="border rounded-xl p-4 text-center bg-white">
              <Users className="w-5 h-5 text-blue-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-blue-700">{fmtMoney(data.summary.total_awarded)}</p>
              <p className="text-xs text-slate-500">Total Awarded</p>
            </div>
            <div className="border rounded-xl p-4 text-center bg-emerald-50 border-emerald-200">
              <TrendingDown className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-emerald-700">{fmtMoney(data.summary.total_savings)}</p>
              <p className="text-xs text-emerald-600">Total Savings</p>
            </div>
            <div className="border rounded-xl p-4 text-center bg-emerald-50 border-emerald-200">
              <TrendingDown className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-emerald-700">{data.summary.saving_pct}%</p>
              <p className="text-xs text-emerald-600">Savings Rate</p>
            </div>
          </div>

          {/* Award Decisions Table */}
          <div className="border rounded-xl bg-white overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50">
              <h2 className="font-bold text-sm">Award Decisions</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-slate-50/50">
                  <SortableTableHeader label="Event" field="event_ref" sortState={trSort} onSort={handleTrSort} />
                  <SortableTableHeader label="Type" field="event_type" sortState={trSort} onSort={handleTrSort} />
                  <SortableTableHeader label="Title" field="event_title" sortState={trSort} onSort={handleTrSort} />
                  <SortableTableHeader label="Awarded To" field="awarded_to" sortState={trSort} onSort={handleTrSort} />
                  <SortableTableHeader label="Awarded By" field="awarded_by" sortState={trSort} onSort={handleTrSort} />
                  <SortableTableHeader label="Baseline" field="baseline" sortState={trSort} onSort={handleTrSort} className="text-right" />
                  <SortableTableHeader label="Awarded" field="awarded_value" sortState={trSort} onSort={handleTrSort} className="text-right" />
                  <SortableTableHeader label="Saving" field="saving_amount" sortState={trSort} onSort={handleTrSort} className="text-right" />
                  <SortableTableHeader label="Method" field="baseline_method" sortState={trSort} onSort={handleTrSort} />
                  <SortableTableHeader label="Requisitions" field="requisitions" sortState={trSort} onSort={handleTrSort} />
                  <SortableTableHeader label="Date" field="awarded_at" sortState={trSort} onSort={handleTrSort} />
                </tr></thead>
                <tbody>
                  {sortData((data.awards || []), trSort.field, trSort.dir).map((a, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="px-3 py-2 font-semibold text-indigo-700">{a.event_ref}</td>
                      <td className="px-3 py-2"><Badge className="text-[9px]">{a.event_type}</Badge></td>
                      <td className="px-3 py-2 truncate max-w-[180px]">{a.event_title}</td>
                      <td className="px-3 py-2">
                        {(a.awarded_to || []).map((s, j) => (
                          <Badge key={j} className="text-[9px] bg-emerald-100 text-emerald-700 mr-0.5">{s}</Badge>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{a.awarded_by}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(a.baseline)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(a.awarded_value)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">{fmtMoney(a.saving_amount)}</td>
                      <td className="px-3 py-2 text-slate-500">{a.baseline_method || '—'}</td>
                      <td className="px-3 py-2">
                        {(a.requisitions || []).map((r, j) => (
                          <Badge key={j} className="text-[9px] bg-blue-100 text-blue-700 mr-0.5">{r.number}</Badge>
                        ))}
                        {(!a.requisitions || a.requisitions.length === 0) && <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-400">{a.awarded_at ? new Date(a.awarded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}</td>
                    </tr>
                  ))}
                  {(!data.awards || data.awards.length === 0) && (
                    <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400">No award decisions recorded yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
